import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { TablePagination } from "@mui/material";
import iconMap from "src/assets/icons/Icons";
import { FontColor, ThemeBasedColorHeader, ThemeFontStyle } from "src/shared/modules/profile/theme/themeCheacker";
import Image from "src/shared/Image";
import { useUser } from "./UserContext";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../hooks/tableConfig";
import FilterListIcon from "@mui/icons-material/FilterList";

type Order = "asc" | "desc";

export interface HeadCell<T> {
  id: keyof T;
  label: string;
  numeric?: boolean;
  disablePadding?: boolean;
  align?: "left" | "center" | "right";
  sorting?: boolean;
}

interface CommonTableProps<T> {
  data: T[];
  headCells: HeadCell<T>[];
  title: string;
  onDeleteSelected?: (selectedIds: (string | number)[]) => void;
  onEditSelected?: (id: string | number) => void;
  denseToggle?: boolean;
  selectable?: boolean;
  onRowClick?: (id: string | number) => void;
  pageSize?: number;
  pagenation?: boolean;
  showFilterIcon?: boolean;
  showToolbar?: boolean;
  denseDefault?: boolean;
  theme?: boolean;
  className?: string;
  Action?: boolean;
  shortByTable?: boolean;
  OrderType?: (data: { orderby: string; field: string }) => void;
  enabileFilter?: boolean;
  onFilter?: () => void;
  defaultSort?: {
    field: keyof T;
    order: Order; // "asc" | "desc"
  };
}

export default function CommonTable<T extends { id: string | number }>({
  data,
  headCells,
  title,
  onDeleteSelected,
  onEditSelected,
  denseToggle = true,
  selectable = true,
  onRowClick,
  pageSize = 10,
  pagenation = true,
  showFilterIcon = true,
  showToolbar = true,
  denseDefault = true,
  theme = true,
  className = "",
  Action = false,
  shortByTable = true,
  OrderType,
  enabileFilter = false,
  onFilter,
  defaultSort,
}: CommonTableProps<T>) {
  const { EditGro, Delete } = iconMap;
  const bgClass = ThemeBasedColorHeader();
  const textClass = FontColor();
  const fontClass = ThemeFontStyle();
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof T>(headCells[0].id);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [dense, setDense] = useState(denseDefault ?? true);
  const { userConfigration } = useUser();

  useEffect(() => {
    setRowsPerPage(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (defaultSort) {
      setOrder(defaultSort.order);
      setOrderBy(defaultSort.field);
    }
  }, [defaultSort]);

  useEffect(() => {
    // setRowsPerPage(userConfigration?.tableDefultSize);
    if (userConfigration?.tableDefultSize) {
      setRowsPerPage(userConfigration.tableDefultSize);
    } else {
      setRowsPerPage(pageSize);
    }
  }, [userConfigration]);

  const handleSort = (property: keyof T) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setOrder(newOrder);
    setOrderBy(property);

    // ✅ If local sorting enabled
    if (shortByTable) return;

    // ✅ Backend sorting trigger

    const sortData = {
      orderby: newOrder,
      field: String(property),
    };

    if (OrderType) OrderType(sortData);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelected(data.map((n) => n.id));
    else setSelected([]);
  };

  const handleSelect = (id: string | number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ✅ Local sorting logic
  const sortedData = useMemo(() => {
    if (!shortByTable) return data;

    const comparator = (a: T, b: T) => {
      if (b[orderBy] < a[orderBy]) return order === "desc" ? -1 : 1;
      if (b[orderBy] > a[orderBy]) return order === "desc" ? 1 : -1;
      return 0;
    };

    return [...data].sort(comparator);
  }, [data, order, orderBy, shortByTable]);

  const visibleRows = pagenation ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sortedData;

  const isSelected = (id: string | number) => selected.includes(id);

  // Helper function to get alignment class
  const getAlignmentClass = (align?: "left" | "center" | "right") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      case "left":
      default:
        return "text-left";
    }
  };

  return (
    <div className={`w-[95%] mx-auto ${fontClass} ${className}`}>
      {/* Toolbar */}
      {showToolbar && (
        <div
          className={`flex items-center justify-between p-3 rounded-t-md border-b 
            ${selected.length > 0 ? `${bgClass} text-white` : `bg-transparent ${textClass}`}`}
        >
          <h2 className='font-semibold text-lg'>{selected.length > 0 ? `${selected.length} selected` : title}</h2>
          <div className='flex items-center gap-2'>
            {selected.length === 1 && onEditSelected && (
              <button onClick={() => onEditSelected(selected[0])} className={`px-2 py-1 rounded ${textClass} hover:${bgClass} hover:text-white`}>
                ✏️
              </button>
            )}
            {selected.length > 0 && onDeleteSelected && (
              <button onClick={() => onDeleteSelected(selected)} className='p-2 text-red-500 hover:text-red-700'>
                🗑️
              </button>
            )}
            {enabileFilter && onFilter && (
              <button onClick={() => onFilter()} className='p-2 text-red-500 hover:text-red-700'>
                <FilterListIcon />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className='overflow-x-auto border border-gray-200 rounded-b-md'>
        <table className='min-w-full text-sm border-collapse'>
          <thead className={`h-12 ${fontClass} ${bgClass}`}>
            <tr>
              {selectable && (
                <th className='px-3 py-2 text-left w-fit'>
                  <input
                    type='checkbox'
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {headCells.map((head) => (
                <th
                  key={String(head.id)}
                  onClick={() => head.sorting !== false && handleSort(head.id)} // ✅ disable click if sorting=false
                  className={`px-3 py-3 select-none ${head.sorting === false ? "" : "cursor-pointer"} ${getAlignmentClass(head.align)}`}
                >
                  <div
                    className={`flex items-center gap-1 ${
                      head.align === "center" ? "justify-center" : head.align === "right" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <span>{head.label}</span>
                    {/* show arrow only if sorting enabled */}
                    {head.sorting !== false && orderBy === head.id && <span>{order === "asc" ? "▲" : "▼"}</span>}
                  </div>
                </th>
              ))}
              {Action && <th className='border-b text-center'>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => (
                <tr
                  key={row.id}
                  className={`${isSelected(row.id) ? "bg-blue-50" : "hover:bg-gray-50"} ${selectable || onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => {
                    if (selectable) handleSelect(row.id);
                    else if (onRowClick) onRowClick(row.id);
                  }}
                >
                  {selectable && (
                    <td className='px-3 py-2'>
                      <input type='checkbox' checked={isSelected(row.id)} onChange={() => handleSelect(row.id)} className='accent-blue-600' />
                    </td>
                  )}
                  {headCells.map((cell) => (
                    <td key={String(cell.id)} className={`px-3 py-3.5 border-t ${getAlignmentClass(cell.align)}`}>
                      {(() => {
                        // ✅ 1. Handle S.No (always 1,2,3 based on visible rows)

                        const value = row[cell.id];

                        // ✅ 2. JSX already provided (orderId, status, etc.)
                        if (React.isValidElement(value)) return value;

                        // ✅ 3. Image column
                        if (cell.id === "Image" && typeof value === "string") {
                          return (
                            <div
                              className={`w-12 h-12 object-cover rounded ${
                                cell.align === "center" ? "mx-auto" : cell.align === "right" ? "ml-auto mr-0" : ""
                              }`}
                            >
                              <Image imagePath={value} />
                            </div>
                          );
                        }

                        // ✅ 4. Default render
                        return String(value ?? "");
                      })()}
                    </td>
                  ))}

                  {Action && (
                    <td className='p-2 border-b text-left'>
                      <div className='w-full h-full flex justify-center gap-4'>
                        <Tooltip title='Edit' arrow>
                          <span>
                            <button
                              className='text-yellow-600'
                              onClick={() => {
                                if (onEditSelected) onEditSelected(row.id);
                              }}
                            >
                              <EditGro />
                            </button>
                          </span>
                        </Tooltip>
                        <Tooltip title='Delete' arrow>
                          <span>
                            <button
                              onClick={() => {
                                if (onDeleteSelected) onDeleteSelected([row.id]);
                              }}
                            >
                              <Delete />
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headCells.length + (selectable ? 1 : 0)} className='text-center py-4 text-black-600'>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagenation && (
        <TablePagination
          rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
          component='div'
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}

      {/* Dense Toggle (Hidden by default) */}
      {denseToggle && (
        <div className='hidden'>
          <label className='flex items-center gap-2 mt-2'>
            <input type='checkbox' checked={dense} onChange={(e) => setDense(e.target.checked)} />
            Dense padding
          </label>
        </div>
      )}
    </div>
  );
}
